import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Unauthorized from '../components/Unauthorized';
import api from '../services/api';

interface Deposito {
  cod_dep: number;
  descricao: string;
  nome_completo: string;
}

const Background = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #0059b2 0%, #009e60 100%)',
  padding: '1rem',
});

const RegisterCard = styled(Card)({
  width: '100%',
  maxWidth: 460,
  borderRadius: 20,
  boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
  textAlign: 'center',
});

const RegisterPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState(1);
  const [depositoId, setDepositoId] = useState<number | ''>('');
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [roleName, setRoleName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        const rn = parsed.role_name ?? parsed.role?.name ?? '';
        setRoleName(rn.trim().toLowerCase());
      } catch (e) {
        console.error('Erro ao parsear user:', e);
      }
    }
  }, []);

  // Requisitos de senha
  const requirements = [
    { label: 'Mínimo 8 caracteres', test: (pw: string) => pw.length >= 8 },
    { label: 'Pelo menos 1 letra maiúscula', test: (pw: string) => /[A-Z]/.test(pw) },
    { label: 'Pelo menos 1 caractere especial (!@#$...)', test: (pw: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pw) },
  ];

  const isStrongPassword = (pw: string) => requirements.every(r => r.test(pw));
  const canSubmit = username && email && password && isStrongPassword(password) && (roleId === 1 || depositoId);

  // Buscar depósitos apenas se não for admin
  useEffect(() => {
    const fetchDepositos = async () => {
      try {
        const res = await api.get('/depositos');
        setDepositos(res.data);
      } catch (err: any) {
        console.error('Erro ao buscar depósitos:', err);
      }
    };
    if (roleId !== 1) fetchDepositos();
  }, [roleId]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setMessage('');

    try {
      await authService.register(
        username,
        email,
        password,
        roleId,
        roleId !== 1 && depositoId !== '' ? Number(depositoId) : undefined
      );

      setMessage('Registro bem-sucedido! Redirecionando para o login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Erro ao registrar usuário');
    } finally {
      setLoading(false);
    }
  };

  if (roleName !== 'admin') {
    return <Unauthorized />;
  }

  return (
    <Background>
      <RegisterCard>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Box component="img" src="/images/logo_nimo_energia_quadrada.png" alt="Nimo Energia" mt={3} sx={{ height: { xs: 50, sm: 60 }, objectFit: 'contain' }} />
            <Typography variant="h5" fontWeight="bold" mb={1} mt={3}>Criar nova conta</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>Preencha os campos abaixo para se registrar</Typography>

            <Box component="form" onSubmit={handleRegister} width="95%" display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Usuário"
                variant="outlined"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value.toUpperCase())}
              />
              <TextField
                label="Email"
                type="email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="Senha"
                type="password"
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <List dense>
                {requirements.map((r) => {
                  const passed = r.test(password);
                  return (
                    <ListItem key={r.label}>
                      <ListItemIcon>
                        {passed ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText primary={r.label} primaryTypographyProps={{ color: passed ? 'green' : 'red', fontSize: 14 }} />
                    </ListItem>
                  );
                })}
              </List>

              <TextField select label="Função" value={roleId} onChange={(e) => setRoleId(Number(e.target.value))} fullWidth>
                <MenuItem value={1}>Administrador</MenuItem>
                <MenuItem value={2}>Motorista</MenuItem>
                <MenuItem value={3}>Faturista</MenuItem>
              </TextField>

              {roleId !== 1 && (
                <FormControl fullWidth>
                  <InputLabel id="deposito-select-label">Depósito</InputLabel>
                  <Select
                    labelId="deposito-select-label"
                    value={depositoId}
                    onChange={(e) => setDepositoId(Number(e.target.value))}
                  >
                    {depositos.map((d) => (
                      <MenuItem key={d.cod_dep} value={d.cod_dep}>{d.nome_completo}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {message && <Alert severity={message.includes('sucesso') ? 'success' : 'error'}>{message}</Alert>}

              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{ mt: 1, mb: 3, py: 1.2, borderRadius: 2, fontWeight: 'bold', backgroundColor: '#007b5f', '&:hover': { backgroundColor: '#00694f' } }}
                disabled={loading || !canSubmit}
              >
                {loading ? <CircularProgress size={26} color="inherit" /> : 'Registrar'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </RegisterCard>
    </Background>
  );
};

export default RegisterPage;
